import React from "react";
import SEO from "../components/SEO";

const TermsOfService = () => {
  return (
    <div className="max-w-5xl mx-auto p-6 text-gray-800 leading-relaxed">
      <SEO
        title="Terms of Service | InvestorLanes"
        description="Read the Terms of Service governing your use of InvestorLanes, including subscriptions, user obligations, and South African legal compliance frameworks."
        canonicalUrl="https://www.investorlanes.com/terms-of-service"
      />

      <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
      <p className="text-gray-500 text-sm mb-6"><strong>Last Updated:</strong> August 2026</p>

      <p className="mb-6">
        Welcome to <strong>InvestorLanes</strong> ("the Platform"). These Terms of Service ("Terms") constitute a legally binding agreement 
        between you ("the User", "Founder", or "Investor") and InvestorLanes, operated by <strong> InvestorLanes </strong> 
        headquartered in <strong>Midrand, Johannesburg, South Africa</strong>.
      </p>

      <p className="mb-6">
        By creating an account, accessing our deal-flow features, or processing a transaction via our payment infrastructure, you confirm that 
        you have read, understood, and agree to be bound by these Terms, along with our Privacy Policy and Refund Policy. If you disagree with any 
        part of these provisions, you must immediately cease using the Platform.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">1. Mandatory Information (ECTA Section 43)</h2>
      <p className="mb-4">
        In terms of Section 43 of the South African Electronic Communications and Transactions Act (ECTA) No. 25 of 2002, the legal 
        identifying credentials of the service provider are outlined below:
      </p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li><strong> Name:</strong>  InvestorLanes </li>
        <li><strong>Registration Number:</strong> Sole Proprietor </li>
        <li><strong> Address:</strong>  Midrand, Johannesburg, South Africa </li>
        <li><strong>Official Website:</strong> https://www.investorlanes.com</li>
        <li><strong>Contact Email:</strong> support@investorlanes.com</li>
      </ul>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">2. Nature of Services and Financial Disclaimer</h2>
      <p className="mb-4">
        InvestorLanes operates strictly as a pan-African match-making digital directory. We facilitate peer-to-peer business networking, introductions, 
        and deal-flow discoveries between independent operational entities. 
      </p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>InvestorLanes <strong>is not</strong> a bank, financial services provider, asset manager, broker-dealer, or licensed financial institution.</li>
        <li>We do not offer crowdfunding pools, investment advice, financial mediation, or capital-raising solicitation.</li>
        <li>Nothing hosted on the Platform constitutes a financial offering or professional advisory service under the South African Financial Advisory and Intermediary Services Act (FAIS), No. 37 of 2002.</li>
        <li>All due diligence, contract verification, regulatory filing, and capital transfers are executed entirely at the independent risk of the User outside of the Platform.</li>
      </ul>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">3. Eligibility and Account Controls</h2>
      <p className="mb-6">
        To activate a functional profile, you must be a minimum of 18 years old and possess the explicit capacity to enter into legally binding business contracts. 
        You are solely accountable for shielding your profile credentials and for any platform actions taken under your account name. Providing deliberately fraudulent, 
        deceptive, or unverified operational data will cause your account to be immediately suspended or blacklisted.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">4. Subscription Billing </h2>
      <p className="mb-4">
        InvestorLanes utilizes premium tier models to unlock structural matchmaking assets. By purchasing a subscription, you acknowledge the following parameters:
      </p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li><strong>Payment Routing:</strong> All processing is handled securely via our <strong>Payment agency partners</strong>. Transactions are executed under international PCI-DSS compliance frameworks.</li>
        <li><strong>Recurring Terms:</strong> Subscription accounts operate on a recurring automated billing cycle (monthly or annually). Your card on file will be debited automatically at the start of each renewal window.</li>
        <li><strong>Cancellation Rights:</strong> You retain the absolute right to cancel your premium plan at any time. Cancellations must be triggered directly via your Platform Account Settings dashboard. Your premium features will remain active until the end of your current paid billing period.</li>
        <li><strong>Price Revisions:</strong> We reserve the right to modify subscription access costs. Any upward adjustments will be communicated to your registered email 30 days prior to implementation.</li>
      </ul>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">5. Acceptable Use and Community Standards</h2>
      <p className="mb-4">When managing an active profile on InvestorLanes, you explicitly agree that you will not:</p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>Publish fictitious startups, falsified revenue metrics, or unverified investor credentials.</li>
        <li>Transmit spam message distributions or unsolicited marketing pitches to other Platform users.</li>
        <li>Deploy automated scrapers, data-mining engines, or bots to crawl or copy directories and listings.</li>
        <li>Engage in illegal money-laundering schemes or breach regional exchange control regulations.</li>
      </ul>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">6. Intellectual Property Rights</h2>
      <p className="mb-6">
        The InvestorLanes brand, source code mechanics, layout parameters, directory indexing setups, custom text files, graphics, and data structural formats are 
        the exclusive intellectual property of InvestorLanes. Users may not copy, harvest, replicate, or exploit any part of our platform assets without our express written consent.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">7. Limitation of Liability and Indemnity</h2>
      <p className="mb-6">
        To the maximum extent permitted under Section 43 of the ECTA and other South African commercial laws, InvestorLanes, its directors, and employees 
        shall not be held liable for any direct, indirect, incidental, punitive, or consequential damages resulting from platform downtime, communication failures, 
        inaccurate listings, or failed business capital deals. You agree to indemnify and hold InvestorLanes harmless against any legal actions or financial losses 
        arising from relationships formed through this Platform.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">8. Breach and Account Termination</h2>
      <p className="mb-6">
        We reserve the absolute right to revoke API access, suspend listings, or completely terminate accounts without prior warning if a user breaches 
        these Terms, violates regional laws, or performs activities that threaten the operational safety of our payment partners and community ecosystem.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">9. Governing Law and Dispute Jurisdiction</h2>
      <p className="mb-6">
        These Terms shall be interpreted, governed, and constructed exclusively in accordance with the laws of the <strong>Republic of South Africa</strong>. 
        Any formal legal disputes, arbitration processes, or litigation actions arising from your platform usage will fall under the exclusive jurisdiction of the 
        <strong> High Court of South Africa (Gauteng Local Division, Johannesburg)</strong>.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">10. Contact Channels</h2>
      <p className="mb-4">For urgent legal correspondence or platform compliance tracking, contact our administrative office:</p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 inline-block min-w-[320px]">
        <p className="mb-1"><strong>Legal Entity:</strong> InvestorLanes </p>
        <p className="mb-1"><strong>Email:</strong> support@investorlanes.com</p>
        <p><strong>Physical Desk:</strong> Midrand, Johannesburg, South Africa</p>
      </div>
    </div>
  );
};

export default TermsOfService;
